/**
 * Operations-dashboard hero: the desktop right-hand stat cards (digital
 * alerts, static alerts, offline units, maintenance logs), plus the
 * mobile-only inline duplicate of the left-hand card set — folded in here
 * because both live inside the same responsive flex child in the page's
 * hero row.
 *
 * Contains:
 * - OperationsStatCardsRight — default export
 */

import BuildIcon from '@mui/icons-material/Build';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import { GRADIENTS } from '@sophocles/features/dashboards/chipStyles';
import { formatSiteHours, formatTrendLabel, formatTrendPrevious } from '@sophocles/features/dashboards/opsHelpers';

import { sameAlarmSelection } from '../utils/alarmTreeOptions';

export default function OperationsStatCardsRight({
  t,
  data,
  loading,
  hoursLabel,
  resolvedCount,
  newAlarmsCount,
  clearedChange,
  offlineChange,
  maintenanceChange,
  maintenanceOpenCount,
  maintenanceDoneCount,
  alarmSitesChange,
  impactedDigitalsChange,
  impactedStaticsChange,
  activeTab,
  selectedAlarms,
  structureFilter,
  alarmStatus,
  alertingKeys,
  offlineAlarmKeys,
  relayLoadTerm,
  setActiveTab,
  setSelectedAlarms,
  setStructureFilter,
  setAlarmStatus,
  showTrend,
}) {
  // Guard the empty/empty case: before the reference catalog loads, both
  // sides are `[]` and would otherwise read as "selected" by set equality.
  const isAlertingSelected = alertingKeys.length > 0 && sameAlarmSelection(selectedAlarms, alertingKeys);
  const isOfflineSelected = offlineAlarmKeys.length > 0 && sameAlarmSelection(selectedAlarms, offlineAlarmKeys);
  const theme = useTheme();
  // Below lg the left-hand column is hidden, so its three cards join this group
  // as siblings — the hero reads as one card set on narrow screens whether that
  // set lays out as a grid or scrolls as a carousel.
  const showLeftCardsInline = useMediaQuery(theme.breakpoints.down('lg'));
  const ps = data?.period_stats;

  return (
    <StatCardGroup
      columns={{ xs: 2, sm: 4 }}
      gap={0.75}
      gridSx={{
        width: { lg: 240 },
        flexShrink: 0,
        display: { lg: 'flex' },
        flexDirection: 'column',
        minHeight: { lg: 320 },
      }}
    >
      <StatCard
        title={t('operationsDashboard.digitalAlerts', { defaultValue: 'Alarming Digital Screens' })}
        value={data?.affected_digitals?.count ?? '-'}
        color="error.main"
        gradient={GRADIENTS.error}
        loading={loading}
        icon="square"
        helperText={
          data?.affected_digitals
            ? t('operationsDashboard.impactedHours', {
                hours: data.affected_digitals.hours,
                defaultValue: `${formatSiteHours(data.affected_digitals.hours)} impacted`,
              })
            : null
        }
        change={impactedDigitalsChange}
        previousValue={data?.affected_digitals?.previous_count}
        onClick={() => {
          setAlarmStatus('all');
          if (structureFilter === 'digital' && isAlertingSelected) {
            setSelectedAlarms([]);
            setStructureFilter(null);
            return;
          }
          setActiveTab('alarms');
          setSelectedAlarms(alertingKeys);
          setStructureFilter('digital');
        }}
        active={structureFilter === 'digital' && isAlertingSelected}
        tooltip={t('operationsDashboard.tooltipDigitalAlerts', {
          defaultValue:
            'Count of digital structures with an active alert in the selected window (structures, not individual alarms).',
        })}
      />
      <StatCard
        title={t('operationsDashboard.staticAlerts', {
          term: relayLoadTerm.plural,
          defaultValue: `Alarming Static ${relayLoadTerm.plural}`,
        })}
        value={data?.affected_statics?.face_count ?? '-'}
        color="error.main"
        gradient={GRADIENTS.errorDeep}
        loading={loading}
        icon={<PowerOffIcon />}
        helperText={
          data?.affected_statics
            ? t('operationsDashboard.impactedHours', {
                hours: data.affected_statics.hours,
                defaultValue: `${formatSiteHours(data.affected_statics.hours)} impacted`,
              })
            : null
        }
        change={impactedStaticsChange}
        previousValue={data?.affected_statics?.previous_face_count}
        onClick={() => {
          setAlarmStatus('all');
          if (structureFilter === 'static' && isAlertingSelected) {
            setSelectedAlarms([]);
            setStructureFilter(null);
            return;
          }
          setActiveTab('alarms');
          setSelectedAlarms(alertingKeys);
          setStructureFilter('static');
        }}
        active={structureFilter === 'static' && isAlertingSelected}
        tooltip={t('operationsDashboard.tooltipStaticAlerts', {
          defaultValue:
            'Count of affected static faces in the selected window (faces, not structures or individual alarms).',
        })}
      />
      <StatCard
        title={`${t('operationsDashboard.offlineUnits', { defaultValue: 'Offline Units' })} (${hoursLabel})`}
        value={data?.period_stats?.offline?.current ?? '-'}
        color="warning.main"
        gradient={GRADIENTS.orange}
        loading={loading}
        icon={<CloudOffIcon />}
        change={offlineChange}
        previousValue={ps?.offline?.previous}
        onClick={() => {
          setAlarmStatus('all');
          if (isOfflineSelected && !structureFilter) {
            setSelectedAlarms([]);
            return;
          }
          setActiveTab('alarms');
          setSelectedAlarms(offlineAlarmKeys);
          setStructureFilter(null);
        }}
        active={isOfflineSelected && !structureFilter}
        tooltip={t('operationsDashboard.tooltipOfflineUnits', {
          defaultValue: 'Units that went offline during the selected time window.',
        })}
      />
      <StatCard
        title={t('operationsDashboard.maintenanceLogs', { defaultValue: 'New Maintenance Logs' })}
        value={maintenanceOpenCount}
        color="info.main"
        gradient={GRADIENTS.info}
        loading={loading}
        icon={<NoteAltIcon />}
        change={maintenanceChange}
        previousValue={ps?.maintenance?.previous}
        subStat={t('operationsDashboard.closedCount', {
          count: maintenanceDoneCount,
          defaultValue: `${maintenanceDoneCount} closed`,
        })}
        subStatColor="success.main"
        onClick={() => {
          setAlarmStatus('all');
          if (activeTab === 'maintenance' && !structureFilter) {
            setActiveTab('alarms');
            return;
          }
          setActiveTab('maintenance');
          setStructureFilter(null);
        }}
        active={activeTab === 'maintenance' && !structureFilter}
        tooltip={t('operationsDashboard.tooltipMaintenanceLogs', {
          defaultValue: 'New maintenance logs opened during the selected time window.',
        })}
      />
      {showLeftCardsInline ? (
        <>
          <StatCard
            title={`${t('operationsDashboard.newAlarms', { defaultValue: 'New Alarms' })} (${hoursLabel})`}
            value={newAlarmsCount}
            color="warning.main"
            gradient={GRADIENTS.warning}
            loading={loading}
            icon={<NotificationsActiveIcon />}
            change={showTrend ? alarmSitesChange : null}
            previousValue={showTrend ? data?.alarm_summary?.previous_total : null}
            changeLabel={showTrend ? formatTrendLabel(alarmSitesChange, t) : undefined}
            previousLabel={showTrend ? formatTrendPrevious(data?.alarm_summary?.previous_total, t) : undefined}
            onClick={() => {
              setAlarmStatus('all');
              if (activeTab === 'alarms' && selectedAlarms.length === 0 && !structureFilter) return;
              setActiveTab('alarms');
              setSelectedAlarms([]);
              setStructureFilter(null);
            }}
            active={activeTab === 'alarms' && alarmStatus === 'all' && selectedAlarms.length === 0 && !structureFilter}
            tooltip={t('operationsDashboard.tooltipNewAlarms', {
              defaultValue: 'New alarms raised during the selected time window.',
            })}
          />
          <StatCard
            title={t('operationsDashboard.activeAlarms', { defaultValue: 'Unresolved Alarms (all)' })}
            value={data?.active_alarms ?? '-'}
            color="error.main"
            gradient={GRADIENTS.error}
            loading={loading}
            icon={<ErrorOutlineIcon />}
            onClick={() => {
              setActiveTab('alarms');
              setSelectedAlarms([]);
              setStructureFilter(null);
              setAlarmStatus(alarmStatus === 'active' ? 'all' : 'active');
            }}
            active={activeTab === 'alarms' && alarmStatus === 'active'}
            tooltip={t('operationsDashboard.tooltipActiveAlarms', {
              defaultValue:
                'The point-in-time open set: an alarm raised at any time that is still active, not scoped to the selected time window.',
            })}
          />
          <StatCard
            title={`${t('operationsDashboard.resolvedAlarms', { defaultValue: 'Resolved Alarms' })} (${hoursLabel})`}
            value={resolvedCount}
            color="success.main"
            gradient={GRADIENTS.success}
            loading={loading}
            icon={<BuildIcon />}
            change={showTrend ? clearedChange : null}
            previousValue={showTrend ? ps?.cleared?.previous : null}
            changeLabel={showTrend ? formatTrendLabel(clearedChange, t) : undefined}
            previousLabel={showTrend ? formatTrendPrevious(ps?.cleared?.previous, t) : undefined}
            invertChange
            onClick={() => {
              setActiveTab('alarms');
              setSelectedAlarms([]);
              setStructureFilter(null);
              setAlarmStatus(alarmStatus === 'resolved' ? 'all' : 'resolved');
            }}
            active={activeTab === 'alarms' && alarmStatus === 'resolved'}
            tooltip={t('operationsDashboard.tooltipResolvedAlarms', {
              defaultValue: 'Alarms cleared during the selected time window.',
            })}
            subStat={resolvedCount > newAlarmsCount ? '✓' : undefined}
            subStatColor={resolvedCount > newAlarmsCount ? 'success.main' : undefined}
          />
        </>
      ) : null}
    </StatCardGroup>
  );
}
